/**
 * Release the body of a `Response` we are about to discard.
 *
 * A `fetch` response body is a stream backed by undici's own chunk queue. Dropping the reference
 * without reading or cancelling it does NOT free that queue promptly: the buffered chunks stay
 * reachable from the stream controller for as long as anything holds the response, and on the
 * server that "anything" is usually the request's render graph. A production heap snapshot showed
 * 987 undici `_Response` objects and 1,720 queued chunks holding 15.4 MB of ArrayBuffer backing
 * store, against only 94 in-flight `Fetch` objects — i.e. mostly bodies nobody was going to read.
 *
 * `cancel()` is the correct disposal for that: it discards the queue and signals upstream that we
 * want no more data, instead of paying to buffer a payload we have already decided to throw away.
 *
 * Always safe to call, and safe to call on a response you did not read:
 * - a null/absent body (204, HEAD) is a no-op;
 * - an already-consumed or already-cancelled body makes `cancel()` reject, which is swallowed —
 *   the point is disposal, so a failure to dispose something already disposed is not an error;
 * - a locked body (a reader is attached, i.e. somebody IS reading it) also rejects and is
 *   swallowed, so this can never yank a body out from under a live consumer.
 *
 * Do NOT call this on a response whose body you still intend to read. Call it on the paths that
 * return early — a non-`ok` status you only need the status code from, or a response superseded by
 * a retry.
 */
export function drainResponseBody(response: Response | null | undefined): void {
    // `body` is null for bodyless responses; `bodyUsed` means someone already read it.
    if (!response?.body || response.bodyUsed) return;
    // Fire-and-forget: callers are on an early-return path and have nothing to await this for.
    void response.body.cancel().catch(() => {
        // Already consumed, already cancelled, or locked by a live reader — nothing to release.
    });
}
